import { AfterViewInit, Component, Input } from "@angular/core";
import { Router } from "@angular/router";

import { Contact, Cryptocoin, CryptoWallet, Fiat, User, ValidationError } from "@models";

import { UserService } from "@main-services/user.service";

import { Helper } from "@helpers/functions";
import { SummaryComponent } from "../../summary/summary";
import { config } from "@config";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { ELIGIBLE_ICO_COINS } from "@helpers/constants";
import { isEmail, truncate } from "@helpers/utils";
import { CryptoTransaction, SendTransaction } from "@models/transaction";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { AccountService } from "@modules/account/services/account.service";
import { ContactService } from "@main-services/contact.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { EMPTY, Observable } from "rxjs";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { CryptoDepositFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit.facade";
import { CryptoDepositStateService } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-state.service";
import { take } from "rxjs/operators";

@Component({
	selector: "send-summary",
	templateUrl: "./send-summary.component.html",
	providers: [CryptoDepositFacade, CryptoDepositStateService],
	standalone: false
})
export class SendSummaryComponent extends SummaryComponent implements AfterViewInit, BpModalHasSave {
	@Input()
	public data: SendTransaction = null;
	@Input()
	public fee: number = 0;
	@Input()
	public needsZeroPrecision: boolean = false;

	private coinValid: boolean = false;
	private checkedICOInvesting: boolean = false;
	private isRecipientEmail: boolean = false;
	private recipientContact: Contact = null;
	private isEligibleICOCoin: boolean = false;

	private cryptoWallet: CryptoWallet = null;
	private cryptocoin: Cryptocoin = null;
	private fiat: Fiat = null;
	public isMultichain$: Observable<boolean> = EMPTY;
	public networkName$: Observable<string> = EMPTY;

	public constructor(
		private apicallService: ApicallService,
		private contactService: ContactService,
		private masterdataService: MasterdataService,
		private router: Router,
		private userService: UserService,
		private tradableListService: TradableListService,
		private translationService: TranslationService,
		private accountService: AccountService,
		private walletService: CryptoWalletService,
		private cryptoDepositFacade: CryptoDepositFacade,
		private launchDarkly: LaunchDarklyService
	) {
		super();
	}

	public ngAfterViewInit(): void {
		// Send summary really doesn't need to be super dynamic, so these can be set in the afterViewInit
		this.isMultichain$ = this.launchDarkly.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN);
		this.cryptoWallet = this.walletService.getItemById(this.data.getWalletId());
		this.cryptocoin = this.tradableListService.getItemById(this.wallet.getCurrencyId()) as Cryptocoin;
		this.launchDarkly
			.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN)
			.pipe(take(1))
			.subscribe((isMultichain) => {
				if (isMultichain) {
					this.networkName$ = this.getNetworkName();
				}
			});
		this.fiat = this.accountService.getDefaultFiat();
		this.recipientContact = this.contactService.findByDestinationTag(
			this.data.getRecipient(),
			this.data.getDestinationTag()
		);
		let actualValue: number;
		if (this.needsZeroPrecision) {
			const precision = this.data.isInternalSendType() ? 8 : this.getCoin().getPrecisionForTx();
			actualValue = truncate(+this.data.getAmountCoin(), precision);
		} else {
			actualValue = +this.data.getAmountCoin();
		}
		if (!this.data.isInternalSendType()) {
			actualValue += this.fee;
		}

		this.setAmountShort("" + actualValue, this.getCoin(), this.needsZeroPrecision);

		const eligibleCoin: string = ELIGIBLE_ICO_COINS.find((name: string) => name === this.getCoin().getSymbol(true));
		this.isEligibleICOCoin = !!eligibleCoin;

		this.isRecipientEmail = isEmail(this.isContact() ? this.getContact().getName() : this.data.getRecipient());
	}

	public get wallet(): CryptoWallet {
		return this.cryptoWallet;
	}

	public set wallet(w: CryptoWallet) {
		this.walletService.upsert(w);
	}

	public isExternalSend(): boolean {
		return this.data.getIsExternal();
	}

	public save(twoFA: string = null): void {
		if (!this.isExternalSend()) {
			this.coinValid = true;
			this.checkedICOInvesting = true;
		}

		if (!this.coinValid) {
			this.wiggle("coinValid");
			return;
		}

		if (this.showICOInvestingSlider() && !this.checkedICOInvesting) {
			this.wiggle("checkedICOInvesting");
			return;
		}

		if (twoFA != null) {
			this.data.authCode = twoFA;
		}

		if (!this.data.getDestinationTag() || this.data.getDestinationTag().trim() === "") {
			this.data.setDestinationTag("");
		}

		this.data["amount"] =
			"" +
			truncate(
				+this.data.getAmountCoin(),
				this.data.isInternalSendType() ? 8 : this.getCoin().getPrecisionForTx()
			);
		const a = new ApicallOption(CryptoTransaction, this.data);
		this.apicallService.post("wallet/" + this.wallet.getId() + "/send", a).subscribe(
			(ans: CryptoTransaction[]) => {
				this.walletService.fetchWallets();
				this.walletService.transactionReceived();
				this.router.navigateByUrl("wallet/" + ans[0].getWalletId() + "/" + ans[0].getId());

				this.finish.emit(ans[0]);
			},
			(error) => {
				if (error instanceof ValidationError) {
					if (this.data.hasError("recipient")) {
						this.setEdit(2);
					}
				}
				Helper.handleError(error);
			}
		);
	}

	public getFiat(): Fiat {
		return this.fiat;
	}

	public getCoin(): Cryptocoin {
		return this.cryptocoin;
	}

	public isHoverable(): boolean {
		return !this.accepted && !this.needs2fa();
	}

	public showSliders(): boolean {
		return this.isExternalSend() && !this.accepted;
	}

	public isContact(): boolean {
		return this.getContact() && !this.getContact().isDeleted();
	}

	public getSummaryToolTipLink(): string {
		return this.translationService.getHdLink() + config.externalPaths["send"]["tooltip"];
	}

	public TwoFAClick(): void {
		if (!this.isExternalSend()) {
			this.coinValid = true;
		}
		if (!this.coinValid) {
			this.wiggle("coinValid");
			return;
		}
		this.accepted = true;
	}

	public isConfirmButtonDisabled(): boolean {
		return (
			(!this.coinValid && this.isExternalSend()) ||
			(this.showICOInvestingSlider() && this.isExternalSend() ? !this.checkedICOInvesting : false)
		);
	}

	public isEmail(): boolean {
		return this.isRecipientEmail;
	}

	public getContact(): Contact {
		return this.recipientContact;
	}

	public needs2fa(): boolean {
		return this.user.get2FA();
	}

	public get user(): User {
		return this.userService.user;
	}

	public setEdit(e: number): void {
		if (this.accepted && this.needs2fa()) {
			return;
		}
		this.edit.emit(e);
	}

	public showICOInvestingSlider(): boolean {
		return this.isEligibleICOCoin;
	}

	public getHeaderTitle(): string {
		return this.needs2fa() && this.accepted ? "confirm_transaction" : "send_to_CRYPTOSYMBOL_contact";
	}

	public get feeAmountFiat(): number {
		return this.toFiatAmount(this.fee);
	}

	public toFiatAmount(coinAmount: number): number {
		return convertTradableToFiat(this.cryptocoin, coinAmount, this.fiat);
	}

	private getNetworkName(): Observable<string> {
		return this.cryptoDepositFacade.getNetworkName(this.cryptocoin.getId());
	}
}
